import { useEffect, useReducer, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { v4 as uuid } from 'uuid';
/* import { addPatchOperations, addPendingChange } from '../../../features/patches/patchesSlice'; */
import type { RootState } from '../../../app/store';
import {
  rangesFromWeightPriceRanges,
  validateRanges,
  weightPriceRangesFromRanges,
} from '../utils/priceRanges';
import { parseCentsInput } from '../utils/namedPriceUtils';

// Define types for the form
export type { RangeItem } from '../utils/priceRanges';

export type Bundle = {
  bundleId: string;
  internalName: string;
  [key: string]: any;
};

export type Product = {
  productId: string;
  [key: string]: any;
};

export type Variant = {
  variantId: string;
  [key: string]: any;
};

export type Slot = {
  slotId: string;
  [key: string]: any;
};

export type Modifier = {
  modifierId: string;
  [key: string]: any;
};

export type Weight = {
  weightId: string;
  [key: string]: any;
};

export type ProductVariantRow = {
  id: string;
  product: Product | null;
  variants: Variant[];
};

export type OperationFormProps = {
  mode?: 'create' | 'edit' | 'remove';
  initialEdit?: any;
  operationTypes?: string[];
  availablePatches?: any[];
  menu: any; // <-- Add menu as a required prop
};

// Form state & actions
type FormState = {
  operationType: string | null;
  data: Record<string, any>;
};
type Action =
  | { type: 'RESET' }
  | { type: 'SET_TYPE'; operationType: string }
  | { type: 'INIT'; payload: Record<string, any> }
  | { type: 'UPDATE_FIELD'; field: string; value: any };

// Reducer to manage all form fields in one object
function formReducer(state: FormState, action: Action): FormState {
  switch (action.type) {
    case 'RESET':
      return { operationType: null, data: {} };
    case 'SET_TYPE':
      return { operationType: action.operationType, data: {} };
    case 'INIT':
      return { ...state, data: action.payload };
    case 'UPDATE_FIELD':
      return {
        ...state,
        data: { ...state.data, [action.field]: action.value },
      };
    default:
      return state;
  }
}

// Removed unused getVariantName function - variant name building is now handled in the enrichment process

/**
 * The form's raw state with every field defaulted to the empty shape its type implies.
 *
 * `SET_TYPE` resets `data` to `{}`, so a freshly chosen operation type has `undefined` everywhere.
 * The validators below are written against arrays - several reach straight for `.length` - so they
 * have to be handed this, not the raw state, or a fresh form throws instead of returning false. It is
 * also what `fields` renders from, which is the point: validation and display must not disagree about
 * what the form currently holds.
 */
const withFormDefaults = (data: Record<string, any>) => ({
  ...data,
  product: data.product || null,
  bundle: data.bundle || [],
  variants: data.variants || [],
  productVariantRows: data.productVariantRows || [{ id: uuid(), product: null, variants: [] }],
  slot: data.slot || null,
  modifiers: data.modifiers || [],
  weights: data.weights || [],
  namedPrices: data.namedPrices || [],
  price: data.price || '',
  exclude: data.exclude || false,
  excludeFromVariant: data.excludeFromVariant ?? true,
  ranges: data.ranges || [{ id: uuid(), start: '0', end: '', price: '', applyModifierWeightUpcharge: true }],
  rangeStart: data.rangeStart || '',
  rangeEnd: data.rangeEnd || '',
});

/**
 * Whether a typed price is a valid `authoringPrice.amount`.
 *
 * That field is a `NonNegativeInt` of whole cents, so a fractional entry is rejected on the wire.
 * `parseCentsInput` is the same parser the named-price editor uses - the two must not disagree about
 * what a price is. It also rejects blank, which a bare `!isNaN(+price)` test did not: `+'' === 0`, so
 * an empty field used to validate and submit a price of $0.00.
 */
const isValidCentsEntry = (price: unknown): boolean =>
  typeof price === 'string' && parseCentsInput(price) !== null;

// Config map: one entry per operation type, with init/validate/build
const operationConfig: Record<
  string,
  {
    init?: (edit: any) => Record<string, any>;
    validate: (data: Record<string, any>, mode?: string) => boolean;
    build: (data: Record<string, any>, mode?: string) => any[];
  }
> = {
  SetBundlePriceMenuPatchOperation: {
    init: (edit) => ({
      bundle: edit.bundle ? [edit.bundle] : [],
      price: edit.price?.amount?.toString() || '',
    }),
    validate: ({ bundle, price }, mode) => {
      if (!(Array.isArray(bundle) && bundle.length > 0)) return false;
      if (mode === 'remove') return true;
      return isValidCentsEntry(price);
    },
    build: ({ bundle, price }, mode) =>
      bundle.map((b: any) => {
        const operation: any = {
          __typename: 'SetBundlePriceMenuPatchOperation',
          setBundlePrice: {
            bundle: { bundleId: b.bundleId },
          },
        };
        if (mode !== 'remove') {
          operation.setBundlePrice.authoringPrice = { amount: +price };
        }
        return operation;
      }),
  },

  SetVariantPriceMenuPatchOperation: {
    init: (edit) => ({
      product: edit.product,
      variants: edit.variant ? [edit.variant] : [],
      price: edit.price?.amount?.toString() || '',
    }),
    validate: ({ product, variants, price }, mode) => {
      if (mode === 'remove') {
        return product != null && Array.isArray(variants) && variants.length > 0;
      }
      return product != null && Array.isArray(variants) && variants.length > 0 && isValidCentsEntry(price);
    },
    build: ({ product, variants, price }, mode) =>
      variants.map((v: any) => {
        const operation: any = {
          __typename: 'SetVariantPriceMenuPatchOperation',
          setVariantPrice: {
            product: { productId: product.productId },
            variant: {
              variantId: v.variantId,
            },
          },
        };
        if (mode !== 'remove') {
          operation.setVariantPrice.authoringPrice = {
            amount: +price,
          };
        }
        return operation;
      }),
  },

  // Named pricing: a store-level override of an org named price, applied to every product/variant
  // that resolves to it. No product/variant selection — the store scope comes from the patches.
  SetNamedPriceAmountPatchOperation: {
    init: (edit) => ({
      namedPrices: edit.namedPrice ? [edit.namedPrice] : [],
      price: edit.amount != null ? edit.amount.toString() : '',
    }),
    validate: ({ namedPrices, price }, mode) => {
      const selected = Array.isArray(namedPrices) && namedPrices.length > 0;
      if (mode === 'remove') return selected;
      // NonNegativeInt on the API - amounts are whole cents.
      return selected && isValidCentsEntry(price);
    },
    build: ({ namedPrices, price }, mode) =>
      namedPrices.map((np: any) => {
        const operation: any = {
          __typename: 'SetNamedPriceAmountPatchOperation',
          setNamedPriceAmount: {
            namedPrice: { namedPriceId: np.namedPriceId, name: np.name },
          },
        };
        if (mode !== 'remove') {
          operation.setNamedPriceAmount.amount = +price;
        }
        return operation;
      }),
  },

  SetModifierPriceRangesMenuPatchOperation: {
    init: (edit) => ({
      productVariantRows: [{ id: uuid(), product: edit.product ?? null, variants: edit.variant ? [edit.variant] : [] }],
      slot: edit.slot,
      ranges: rangesFromWeightPriceRanges(edit.weightPriceRanges),
    }),
    validate: ({ productVariantRows, slot, ranges }, mode) => {
      const rowsValid =
        Array.isArray(productVariantRows) &&
        productVariantRows.length > 0 &&
        productVariantRows.every((r: any) => r.product != null && Array.isArray(r.variants) && r.variants.length > 0);
      if (!(rowsValid && slot != null)) return false;
      if (mode === 'remove') return true;
      // The last range may be left open-ended (no end bound) - the API permits it and
      // StoreOperationsDialog already allows it when editing existing ranges; requiring a
      // closed bound here left a correctly-built open-ended range permanently un-saveable.
      return validateRanges(ranges, { allowOpenEndedLast: true });
    },
    build: ({ productVariantRows, slot, ranges }, mode) =>
      (productVariantRows || []).flatMap(({ product, variants }: any) =>
        (variants || []).map((v: any) => {
          const operation: any = {
            __typename: 'SetModifierPriceRangesMenuPatchOperation',
            setModifierPriceRanges: {
              product: { productId: product.productId },
              variant: { variantId: v.variantId },
              slot: { slotId: slot.slotId },
            },
          };
          if (mode !== 'remove' && ranges && Array.isArray(ranges) && ranges.length > 0) {
            operation.setModifierPriceRanges.weightPriceRanges = weightPriceRangesFromRanges(ranges);
          }
          return operation;
        })
      ),
  },

  SetModifierWeightPriceMenuPatchOperation: {
    init: (edit) => ({
      productVariantRows: [{ id: uuid(), product: edit.product ?? null, variants: edit.variant ? [edit.variant] : [] }],
      slot: edit.slot,
      modifiers: edit.modifier ? [edit.modifier] : [],
      weights: edit.weight ? [edit.weight] : [],
      price: edit.price?.amount?.toString() || '',
    }),
    validate: ({ productVariantRows, slot, modifiers, weights, price }, mode) => {
      const rowsValid =
        Array.isArray(productVariantRows) &&
        productVariantRows.length > 0 &&
        productVariantRows.every((r: any) => r.product != null && Array.isArray(r.variants) && r.variants.length > 0);
      if (mode === 'remove') {
        return rowsValid && slot != null && modifiers.length > 0 && weights.length > 0;
      }
      return rowsValid && slot != null && modifiers.length > 0 && weights.length > 0 && isValidCentsEntry(price);
    },
    build: ({ productVariantRows, slot, modifiers, weights, price }, mode) =>
      (productVariantRows || []).flatMap(({ product, variants }: any) =>
        (variants || []).flatMap((v: any) =>
          modifiers.flatMap((m: any) =>
            weights.map((w: any) => {
              const operation: any = {
                __typename: 'SetModifierWeightPriceMenuPatchOperation',
                setModifierWeightPrice: {
                  product: { productId: product.productId },
                  variant: { variantId: v.variantId },
                  slot: { slotId: slot.slotId },
                  modifier: { modifierId: m.modifierId },
                  weight: { weightId: w.modifierWeightId || w.weightId },
                },
              };
              if (mode !== 'remove') {
                operation.setModifierWeightPrice.authoringPrice = { amount: +price };
              }
              return operation;
            })
          )
        )
      ),
  },

  RemoveModifierFromSlotPatchOperation: {
    init: (edit) => ({
      slot: edit.slot,
      modifiers: edit.modifier ? [edit.modifier] : [],
    }),
    validate: ({ slot, modifiers }) => slot != null && modifiers.length > 0,
    build: ({ slot, modifiers }) =>
      modifiers.map((m: any) => ({
        __typename: 'RemoveModifierFromSlotPatchOperation',
        removeModifierFromSlot: {
          slotId: slot.slotId,
          modifierId: m.modifierId,
          slot: {
            slotId: slot.slotId,
            name: slot.name,
            internalName: slot.internalName,
            slotCode: slot.slotCode
          },
          modifier: {
            modifierId: m.modifierId,
            name: m.name,
            internalName: m.internalName
          }
        },
      })),
  },

  RemoveProductMenuPatchOperation: {
    init: (edit) => ({ product: edit.product }),
    // An array, not a single product: this form's ProductSelect is `multiple`, and `build` maps over
    // it. `product != null` also passed an empty selection, which built zero operations and saved
    // nothing - visible only as a Save that appeared to do nothing.
    validate: ({ product }) => Array.isArray(product) && product.length > 0,
    build: ({ product }) => {
      return product.map((p: any) => ({
        __typename: 'RemoveProductMenuPatchOperation',
        removeProduct: {
          product: {
            productId: p.productId,
          },
        },
      }));
    },
  },

  RemoveVariantMenuPatchOperation: {
    init: (edit) => ({
      product: edit.product,
      variants: edit.variant ? [edit.variant] : [],
    }),
    validate: ({ product, variants }) => product != null && variants.length > 0,
    build: ({ product, variants }) =>
      variants.map((v: any) => ({
        __typename: 'RemoveVariantMenuPatchOperation',
        removeVariant: {
          product: {
            productId: product.productId,
          },
          variant: { variantId: v.variantId },
        },
      })),
  },

  RemoveBundleMenuPatchOperation: {
    init: (edit) => ({ bundle: edit.bundle ? [edit.bundle] : [] }),
    validate: ({ bundle }) => Array.isArray(bundle) && bundle.length > 0,
    build: ({ bundle }) =>
      bundle.map((b: any) => ({
        __typename: 'RemoveBundleMenuPatchOperation',
        removeBundle: {
          bundle: { bundleId: b.bundleId },
        },
      })),
  },

  RemoveModifierMenuPatchOperation: {
    init: (edit) => ({ modifiers: edit.modifier ? [edit.modifier] : [] }),
    validate: ({ modifiers }) => modifiers.length > 0,
    build: ({ modifiers }) =>
      modifiers.map((m: any) => ({
        __typename: 'RemoveModifierMenuPatchOperation',
        removeModifier: {
          modifierId: m.modifierId,
          modifier: { // Add the modifier object for enrichment
            modifierId: m.modifierId,
            name: m.name,
            modifierCode: m.modifierCode
          },
        },
      })),
  },

  RemoveWeightFromSlotModifierMenuPatchOperation: {
    init: (edit) => ({
      slot: edit.slot,
      modifiers: edit.modifier ? [edit.modifier] : [],
      weights: edit.weight ? [edit.weight] : [],
    }),
    validate: ({ slot, modifiers, weights }) =>
      slot != null && Array.isArray(modifiers) && modifiers.length > 0 &&
      Array.isArray(weights) && weights.length > 0,
    build: ({ slot, modifiers, weights }) =>
      (modifiers as any[]).flatMap((m: any) =>
        (weights as any[]).map((w: any) => ({
          __typename: 'RemoveWeightFromSlotModifierMenuPatchOperation',
          removeWeightFromSlotModifier: {
            slot:     { slotId: slot.slotId },
            modifier: { modifierId: m.modifierId },
            weight:   { weightId: w.modifierWeightId || w.weightId },
          },
        }))
      ),
  },

  SetModifierWeightIsExcludedFromVariantMenuPatchOperation: {
    init: (edit) => ({
      product: edit.product,
      variants: edit.variant ? [edit.variant] : [],
      slot: edit.slot,
      modifiers: edit.modifier ? [edit.modifier] : [],
      weights: edit.weight ? [edit.weight] : [],
      excludeFromVariant: edit.excludeFromVariant ?? true,
    }),
    validate: ({ product, variants, slot, modifiers, weights }) =>
      product != null && variants.length > 0 && slot != null && modifiers.length > 0 && weights.length > 0,
    build: ({ product, variants, slot, modifiers, weights, excludeFromVariant }) =>
      variants.flatMap((v: any) =>
        modifiers.flatMap((m: any) =>
          weights.map((w: any) => ({
            __typename: 'SetModifierWeightIsExcludedFromVariantMenuPatchOperation',
            setModifierWeightIsExcludedFromVariant: {
              productId: product.productId,
              variantId: v.variantId,
              slotId: slot.slotId,
              modifierId: m.modifierId,
              weightId: w.modifierWeightId || w.weightId,
              isExcludedFromVariant: excludeFromVariant,
              product: {
                productId: product.productId,
                name: product.name,
                internalName: product.internalName
              },
              variant: {
                variantId: v.variantId,
                name: v.name,
                internalName: v.internalName,
                selectedOptions: v.selectedOptions
              },
              slot: {
                slotId: slot.slotId,
                name: slot.name,
                internalName: slot.internalName
              },
              modifier: {
                modifierId: m.modifierId,
                name: m.name,
                internalName: m.internalName
              },
              weight: {
                weightId: w.modifierWeightId || w.weightId,
                name: w.name,
                internalName: w.internalName
              }
            },
          }))
        )
      ),
  },
};

export function useOperationForm({
  mode = 'create',
  initialEdit = null,
  operationTypes: customOperationTypes,
  availablePatches = [],
  addPendingOp,
  menu, // <-- Destructure menu from props
}: OperationFormProps & { addPendingOp?: (opObj: { minimal: any; enriched: any }, type?: 'set' | 'remove') => void }) {
  const { patches, patchObject, patchType, /* pendingChanges */ } = useSelector((s: RootState) => s.patches);
  // availablePatches is now always passed as a prop from OperationDrawer
  const storeOperationTypes = useSelector((s: RootState) => s.patches.operationTypes);
  const operationTypes = customOperationTypes ?? Object.values(storeOperationTypes).flat();

  const [state, dispatch] = useReducer(formReducer, {
    operationType: null,
    data: {},
  });

  // reset on patches change
  useEffect(() => {
    dispatch({ type: 'RESET' });
  }, [patches]);

  // initialize in edit mode
  useEffect(() => {
    const t = initialEdit?.operationType;
    if (mode === 'edit' && t && operationConfig[t]?.init) {
      const payload = operationConfig[t]!.init!(initialEdit);
      dispatch({ type: 'INIT', payload });
      dispatch({ type: 'SET_TYPE', operationType: t });
    }
  }, [mode, initialEdit]);

  const saveOrUpdate = useCallback(() => {
    const { operationType, data } = state;
    if (!operationType) return null;

    const cfg = operationConfig[operationType];
    if (!cfg) {
      console.error(`No config for ${operationType}`);
      return null;
    }

    // Pass mode to build function
    const ops = cfg.build(data, mode);

    // Always use availablePatches for patchIds (dropdown-driven UI)
    const patchIds: string[] = Array.isArray(availablePatches)
      ? availablePatches.map((p: any) => p.patchId).filter(Boolean)
      : [];

    // Map operations for pending changes
    // Import here to avoid circular deps
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const { mapPatchOperations } = require('../utils/mapPatchOperations');
    const mappedSetOps = mapPatchOperations(mode === 'remove' ? [] : ops);
    const mappedRemoveOps = mapPatchOperations(mode === 'remove' ? ops : [], true);
    const finalPayload = {
      patchIds,
      setOperations: mappedSetOps,
      removeOperations: mappedRemoveOps,
    };

    // IMPORTANT: This logging must ALWAYS remain for debugging operations payload
    // DO NOT REMOVE - needed for troubleshooting operation accumulation and API calls
    // eslint-disable-next-line no-console
    console.debug('Payload debug:', JSON.stringify(finalPayload, null, 2));

    // For each op, add to pendingOps (minimalOp, enrichedOp)
    if (addPendingOp) {
      // Helper to enrich an op with display names from availablePatches or form state
      const enrichOp = (op: any): any => {
        // Try to find names for product, variant, bundle, modifier, slot, etc. from availablePatches or bundles (Redux state)
        const findEntity = (type: string, id: string) => {
          for (const patch of availablePatches || []) {
            if (patch[type] && (patch[type].productId === id || patch[type].variantId === id || patch[type].bundleId === id || patch[type].modifierId === id || patch[type].slotId === id)) {
              return patch[type];
            }
            if (patch.productId === id || patch.variantId === id || patch.bundleId === id || patch.modifierId === id || patch.slotId === id) {
              return patch;
            }
          }
          // For bundles, products, and variants, also check Redux state from the menu prop
          if (type === 'bundle' && menu && Array.isArray(menu.bundles)) {
            const found = menu.bundles.find((b: any) => b.bundleId === id);
            if (found) return found;
          }
          if (type === 'product' && menu && Array.isArray(menu.products)) {
            const found = menu.products.find((p: any) => p.productId === id);
            if (found) return found;
          }
          if (type === 'variant' && menu && Array.isArray(menu.products)) {
            for (const product of menu.products) {
              const found = product.variants?.find((v: any) => v.variantId === id);
              if (found) return found;
            }
          }
          return undefined;
        };

        // Deep clone op to avoid mutating original
        const enriched = JSON.parse(JSON.stringify(op));

        // Helper to attach name/internalName/bundleCode/displayName to a target object
        const attachNames = (target: any, entity: any) => {
          if (!target || !entity) return;
          if (entity.name) target.name = entity.name;
          if (entity.internalName) target.internalName = entity.internalName;
          if (entity.bundleCode) target.bundleCode = entity.bundleCode;
          if (entity.selectedOptions) target.selectedOptions = entity.selectedOptions;
          // Attach displayName as 'internalName (bundleCode)' or 'name (bundleCode)' if available
          if (entity.internalName && entity.bundleCode) {
            target.displayName = `${entity.internalName} (${entity.bundleCode})`;
          } else if (entity.name && entity.bundleCode) {
            target.displayName = `${entity.name} (${entity.bundleCode})`;
          } else if (entity.name) {
            target.displayName = entity.name;
          } else if (entity.internalName) {
            target.displayName = entity.internalName;
          } else {
            target.displayName = 'Unknown';
          }
        };

        // Use form data as fallback when availablePatches don't have the info
        const enrichFromFormData = (enriched: any) => {
          // For variant price operations, ensure we have product and variant info
          if (enriched.__typename === 'SetVariantPriceMenuPatchOperation' && enriched.setVariantPrice) {
            if (data.product && !enriched.setVariantPrice.product?.name) {
              attachNames(enriched.setVariantPrice.product, data.product);
            }
            if (data.variants?.[0] && !enriched.setVariantPrice.variant?.name) {
              attachNames(enriched.setVariantPrice.variant, data.variants[0]);
            }
          }

          // Named price operations carry only a named price, which is not part of the menu tree, so
          // its display name has to come from the form selection
          if (enriched.__typename === 'SetNamedPriceAmountPatchOperation' && enriched.setNamedPriceAmount) {
            const selected = (data.namedPrices || []).find(
              (np: any) => np.namedPriceId === enriched.setNamedPriceAmount.namedPrice?.namedPriceId
            );
            if (selected && !enriched.setNamedPriceAmount.namedPrice?.displayName) {
              attachNames(enriched.setNamedPriceAmount.namedPrice, selected);
            }
          }

          // For modifier weight price operations, attach names for all entities
          if (enriched.__typename === 'SetModifierWeightPriceMenuPatchOperation' && enriched.setModifierWeightPrice) {
            // Find the matching product/variant row by productId/variantId
            const matchingRow = Array.isArray(data.productVariantRows)
              ? data.productVariantRows.find((r: any) =>
                  r.product?.productId === enriched.setModifierWeightPrice.product?.productId
                )
              : null;
            const matchingVariant = matchingRow?.variants?.find(
              (v: any) => v.variantId === enriched.setModifierWeightPrice.variant?.variantId
            );
            if (matchingRow?.product && !enriched.setModifierWeightPrice.product?.name) {
              attachNames(enriched.setModifierWeightPrice.product, matchingRow.product);
            }
            if (matchingVariant && !enriched.setModifierWeightPrice.variant?.name) {
              attachNames(enriched.setModifierWeightPrice.variant, matchingVariant);
            }
            if (data.slot && !enriched.setModifierWeightPrice.slot?.name) {
              attachNames(enriched.setModifierWeightPrice.slot, data.slot);
            }
            const matchingModifier = (data.modifiers as any[] | undefined)?.find(
              (m: any) => m.modifierId === enriched.setModifierWeightPrice.modifier?.modifierId
            );
            if (matchingModifier && !enriched.setModifierWeightPrice.modifier?.name) {
              attachNames(enriched.setModifierWeightPrice.modifier, matchingModifier);
            }
            const matchingWeight = (data.weights as any[] | undefined)?.find(
              (w: any) => (w.modifierWeightId || w.weightId) === enriched.setModifierWeightPrice.weight?.weightId
            );
            if (matchingWeight && !enriched.setModifierWeightPrice.weight?.name) {
              attachNames(enriched.setModifierWeightPrice.weight, matchingWeight);
            }
          }

          // For modifier price ranges operations, attach names for all entities
          if (enriched.__typename === 'SetModifierPriceRangesMenuPatchOperation' && enriched.setModifierPriceRanges) {
            const matchingRow = Array.isArray(data.productVariantRows)
              ? data.productVariantRows.find((r: any) =>
                  r.product?.productId === enriched.setModifierPriceRanges.product?.productId
                )
              : null;
            const matchingVariant = matchingRow?.variants?.find(
              (v: any) => v.variantId === enriched.setModifierPriceRanges.variant?.variantId
            );
            if (matchingRow?.product && !enriched.setModifierPriceRanges.product?.name) {
              attachNames(enriched.setModifierPriceRanges.product, matchingRow.product);
            }
            if (matchingVariant && !enriched.setModifierPriceRanges.variant?.name) {
              attachNames(enriched.setModifierPriceRanges.variant, matchingVariant);
            }
            if (data.slot && !enriched.setModifierPriceRanges.slot?.name) {
              attachNames(enriched.setModifierPriceRanges.slot, data.slot);
            }
            // Attach names for each range weight if available
            if (Array.isArray(enriched.setModifierPriceRanges.weightPriceRanges) && Array.isArray(data.weights)) {
              enriched.setModifierPriceRanges.weightPriceRanges.forEach((range: any, idx: number) => {
                if (data.weights[idx] && !range.weight?.name) {
                  if (!range.weight) range.weight = {};
                  attachNames(range.weight, data.weights[idx]);
                }
              });
            }
          }

          // For remove modifier from slot operations, attach names for slot and modifier
          if (enriched.__typename === 'RemoveModifierFromSlotPatchOperation' && enriched.removeModifierFromSlot) {
            if (data.slot && !enriched.removeModifierFromSlot.slot?.name) {
              if (!enriched.removeModifierFromSlot.slot) enriched.removeModifierFromSlot.slot = {};
              attachNames(enriched.removeModifierFromSlot.slot, data.slot);
            }
            if (data.modifiers?.[0] && !enriched.removeModifierFromSlot.modifier?.name) {
              if (!enriched.removeModifierFromSlot.modifier) enriched.removeModifierFromSlot.modifier = {};
              attachNames(enriched.removeModifierFromSlot.modifier, data.modifiers[0]);
            }
          }

          // For remove modifier operations, attach names for modifier
          if (enriched.__typename === 'RemoveModifierMenuPatchOperation' && enriched.removeModifier) {
            if (data.modifiers?.[0] && !enriched.removeModifier.modifier?.name) {
              if (!enriched.removeModifier.modifier) enriched.removeModifier.modifier = {};
              attachNames(enriched.removeModifier.modifier, data.modifiers[0]);
            }
          }

          // For set modifier weight excluded from variant operations, attach names for all entities
          if (enriched.__typename === 'SetModifierWeightIsExcludedFromVariantMenuPatchOperation' && enriched.setModifierWeightIsExcludedFromVariant) {
            if (data.product && !enriched.setModifierWeightIsExcludedFromVariant.product?.name) {
              if (!enriched.setModifierWeightIsExcludedFromVariant.product) enriched.setModifierWeightIsExcludedFromVariant.product = {};
              attachNames(enriched.setModifierWeightIsExcludedFromVariant.product, data.product);
            }
            const matchingExclVariant = (data.variants as any[] | undefined)?.find(
              (v: any) => v.variantId === enriched.setModifierWeightIsExcludedFromVariant.variant?.variantId
            );
            if (matchingExclVariant && !enriched.setModifierWeightIsExcludedFromVariant.variant?.name) {
              if (!enriched.setModifierWeightIsExcludedFromVariant.variant) enriched.setModifierWeightIsExcludedFromVariant.variant = {};
              attachNames(enriched.setModifierWeightIsExcludedFromVariant.variant, matchingExclVariant);
            }
            if (data.slot && !enriched.setModifierWeightIsExcludedFromVariant.slot?.name) {
              if (!enriched.setModifierWeightIsExcludedFromVariant.slot) enriched.setModifierWeightIsExcludedFromVariant.slot = {};
              attachNames(enriched.setModifierWeightIsExcludedFromVariant.slot, data.slot);
            }
            const matchingExclModifier = (data.modifiers as any[] | undefined)?.find(
              (m: any) => m.modifierId === enriched.setModifierWeightIsExcludedFromVariant.modifier?.modifierId
            );
            if (matchingExclModifier && !enriched.setModifierWeightIsExcludedFromVariant.modifier?.name) {
              if (!enriched.setModifierWeightIsExcludedFromVariant.modifier) enriched.setModifierWeightIsExcludedFromVariant.modifier = {};
              attachNames(enriched.setModifierWeightIsExcludedFromVariant.modifier, matchingExclModifier);
            }
            const matchingExclWeight = (data.weights as any[] | undefined)?.find(
              (w: any) => (w.modifierWeightId || w.weightId) === enriched.setModifierWeightIsExcludedFromVariant.weight?.weightId
            );
            if (matchingExclWeight && !enriched.setModifierWeightIsExcludedFromVariant.weight?.name) {
              if (!enriched.setModifierWeightIsExcludedFromVariant.weight) enriched.setModifierWeightIsExcludedFromVariant.weight = {};
              attachNames(enriched.setModifierWeightIsExcludedFromVariant.weight, matchingExclWeight);
            }
          }

          if (enriched.__typename === 'RemoveWeightFromSlotModifierMenuPatchOperation' && enriched.removeWeightFromSlotModifier) {
            if (data.slot && !enriched.removeWeightFromSlotModifier.slot?.name) {
              if (!enriched.removeWeightFromSlotModifier.slot) enriched.removeWeightFromSlotModifier.slot = {};
              attachNames(enriched.removeWeightFromSlotModifier.slot, data.slot);
            }
            const matchingRmvModifier = (data.modifiers as any[] | undefined)?.find(
              (m: any) => m.modifierId === enriched.removeWeightFromSlotModifier.modifier?.modifierId
            );
            if (matchingRmvModifier && !enriched.removeWeightFromSlotModifier.modifier?.name) {
              if (!enriched.removeWeightFromSlotModifier.modifier) enriched.removeWeightFromSlotModifier.modifier = {};
              attachNames(enriched.removeWeightFromSlotModifier.modifier, matchingRmvModifier);
            }
            const matchingRmvWeight = (data.weights as any[] | undefined)?.find(
              (w: any) => (w.modifierWeightId || w.weightId) === enriched.removeWeightFromSlotModifier.weight?.weightId
            );
            if (matchingRmvWeight && !enriched.removeWeightFromSlotModifier.weight?.name) {
              if (!enriched.removeWeightFromSlotModifier.weight) enriched.removeWeightFromSlotModifier.weight = {};
              attachNames(enriched.removeWeightFromSlotModifier.weight, matchingRmvWeight);
            }
          }
        };

        // Recursively enrich all relevant fields in the operation
        const enrichFields = (obj: any) => {
          if (!obj || typeof obj !== 'object') return;
          // Try common entity types
          if (obj.productId) {
            const entity = findEntity('product', obj.productId);
            attachNames(obj, entity);
          }
          if (obj.variantId) {
            const entity = findEntity('variant', obj.variantId);
            attachNames(obj, entity);
          }
          if (obj.bundleId) {
            const entity = findEntity('bundle', obj.bundleId);
            attachNames(obj, entity);
          }
          if (obj.modifierId) {
            const entity = findEntity('modifier', obj.modifierId);
            attachNames(obj, entity);
          }
          if (obj.slotId) {
            const entity = findEntity('slot', obj.slotId);
            attachNames(obj, entity);
          }
          // Recurse into nested objects
          Object.values(obj).forEach((v) => {
            if (typeof v === 'object') enrichFields(v);
          });
        };

        enrichFields(enriched);
        enrichFromFormData(enriched);
        return enriched;
      };

      if (mode === 'remove') {
        ops.forEach((op: any, i: number) => {
          addPendingOp({ minimal: mappedRemoveOps[i] || op, enriched: enrichOp(op) }, 'remove');
        });
      } else {
        ops.forEach((op: any, i: number) => {
          addPendingOp({ minimal: mappedSetOps[i] || op, enriched: enrichOp(op) }, 'set');
        });
      }
    }

    dispatch({ type: 'RESET' });
    return ops;
  }, [state, mode, availablePatches, addPendingOp, menu]);

  const save = useCallback(() => {
    return saveOrUpdate();
  }, [saveOrUpdate]);

  const update = useCallback(() => {
    return saveOrUpdate();
  }, [saveOrUpdate]);

  const remove = useCallback(() => {
    return saveOrUpdate();
  }, [saveOrUpdate]);

  const setField = useCallback((field: string, value: any) => {
    dispatch({ type: 'UPDATE_FIELD', field, value });
  }, []);

  // Create backward-compatible handlers for each field
  const createFieldHandler = useCallback(
    (fieldName: string) => {
      return (value: any) => {
        setField(fieldName, value);
      };
    },
    [setField]
  );

  // Ensure all fields are available in the fields object
  const defaulted = withFormDefaults(state.data);
  const fields = {
    ...defaulted,
    operationType: state.operationType,
    operationTypes,
    menu,
    patches,
    modifier: defaulted.modifiers, // Note: modifiers in state, modifier in API
  };

  const handlers = {
    setOperationType: (t: string | null) =>
      t ? dispatch({ type: 'SET_TYPE', operationType: t }) : dispatch({ type: 'RESET' }),
    setField,
    save,
    update,
    remove,
    reset: () => dispatch({ type: 'RESET' }),
    // Add backward-compatible handlers for common fields
    setProduct: createFieldHandler('product'),
    setBundle: createFieldHandler('bundle'),
    setVariants: createFieldHandler('variants'),
    setProductVariantRows: createFieldHandler('productVariantRows'),
    setSlot: createFieldHandler('slot'),
    setModifier: createFieldHandler('modifiers'),
    setWeights: createFieldHandler('weights'),
    setNamedPrices: createFieldHandler('namedPrices'),
    setPrice: createFieldHandler('price'),
    setRangeStart: createFieldHandler('rangeStart'),
    setRangeEnd: createFieldHandler('rangeEnd'),
    setRanges: createFieldHandler('ranges'),
    setExclude: createFieldHandler('exclude'),
    setExcludeFromVariant: createFieldHandler('excludeFromVariant'),
  };

  const meta = {
    operationType: state.operationType,
    operationTypes,
    mode,
    menu,
    patches,
    patchObject,
    patchType,
  };

  // Add validation function
  const isValid = useCallback(() => {
    const { operationType, data } = state;
    if (!operationType) return false;

    const cfg = operationConfig[operationType];
    if (!cfg) return false;

    // Defaulted, not raw: `data` is `{}` for a freshly chosen type, and validators that reach for
    // `.length` would throw rather than report the form incomplete.
    return cfg.validate(withFormDefaults(data), mode);
  }, [state, mode]);

  return {
    fields,
    handlers,
    meta,
    isValid,
  };
}
